import Jumbotron from '@/components/core/Jumbotron/Jumbotron';
import { Layout } from '@/components/shared/layouts/Layout/Layout';
import { ColorTypeOptions } from '@/components/shared/navigations/CoreNavigation/CoreNavigation';
import { getSafeLocale } from '@/utils/locale';
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import styles from '../../styles/visit.module.scss';
import Map from '@/components/features/map/Map';
import { MapProvider } from '@/components/features/map/MapContext';
import { createClient } from '@/jsonapi/createClient';
import { IMuseumResource } from '@/interfaces/IMuseumResource';
import { serializeCollectionResource } from '@/utils/serilize-resource';
import { MapStatsVisit } from '@/components/features/visit/MapStats/MapStatsVisit';

import Section from '@/components/core/Section/Section';
import Heading from '@/components/core/Heading/Heading';
import SectionColumn from '@/components/core/Section/SectionColumn/SectionColumn';
import Link from 'next/link';
import Button from '@/components/core/Button/Button';
import FeaturedItem from '@/components/features/visit/FeaturedItem/FeaturedItem';
import VisitMuseums from '@/components/features/visit/VisitMuseums/VisitMuseums';
import { MuseumFilterTypes } from '@/enums/MuseumFilterTypes';
import { useTranslation } from 'next-i18next';
import { NextSeo } from 'next-seo';

type VisitProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const VisitPage: NextPage<VisitProps> = ({ museums, pastMuseums, currentMuseums, filterMuseums, clusters }) => {
	const { t } = useTranslation();

	return (
		<>
			<NextSeo title={t('visit')} />
			<Layout colorTypeActive={ColorTypeOptions.PRIMARY_LIGHT}>
				<MapProvider>
					<Jumbotron
						className={styles.visitJumbotron}
						contentClassName={styles.visitJumbotronContent}
						color={Jumbotron.color.PRIMARY}
					>
						<Map
							mapPathClassName={styles.visitMapPath}
							hideClusters
							museums={museums.data}
							clusters={clusters}
							className="u-hidden-mobile"
						/>
						<Map
							mapPathClassName={styles.visitMapPath}
							hideClusters
							museums={museums.data}
							clusters={clusters}
							mapWidth={600}
							mapHeight={592}
							verticalMapOffset={-50}
							className="u-visible-mobile"
						/>
					</Jumbotron>
					<div className={styles.jumbotronOverlay}>
						<div className={styles.jumbotronOverlayContainer}>
							<div className={styles.jumbotronOverlayInfoLeft}>
								<MapStatsVisit
									museums={museums.data}
									pastMuseums={pastMuseums.meta}
									currentMuseums={currentMuseums.data}
								/>
							</div>
							<div className={styles.jumbotronOverlayInfoRight}>
								<div className={styles.infoBoxSecondary}>
									<div className={styles.infoBoxContent}>
										<p className={styles.infoBoxText}>{t('museum-of-broken-relationships-info-box')}</p>
										<p className={styles.infoBoxText}>{t('our-ongoing-travelling-exhibit')}</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</MapProvider>
				<Section>
					<Heading text={t('museums')} />
					<div className={styles.section}>
						<FeaturedItem
							color={FeaturedItem.color.DIMMED}
							align={FeaturedItem.align.LEFT}
							location={FeaturedItem.location.TOP_RIGHT}
							image={['/images/museum-zg.jpg', '/images/museum-zg@2x.jpg']}
							size={FeaturedItem.size.NARROW}
							className={styles.sectionItem}
						>
							<p className={styles.visitMuseumLocation}>{t('zagreb-croatia')}</p>
							<p className={styles.visitMuseumInfo}>
								{`${t('summer-10-am-to-9-pm-cet-ever')} `}
								{t('winter-hours-zg-visit')}
							</p>
							<Link href={'/visit/museum-details/zagreb'}>
								<Button color={Button.color.PRIMARY}>{t('visitor-info')}</Button>
							</Link>
						</FeaturedItem>
						<FeaturedItem
							color={FeaturedItem.color.DIMMED}
							align={FeaturedItem.align.LEFT}
							location={FeaturedItem.location.TOP_RIGHT}
							image={['/images/museum-th.jpg', '/images/museum-th@2x.jpg']}
							size={FeaturedItem.size.NARROW}
							className={styles.sectionItem}
						>
							<p className={styles.visitMuseumLocation}>{t('chiang-mai-thailand')}</p>
							<p className={styles.visitMuseumInfo}>{`${t('thaimuseum.workingHoursShort')}`}</p>
							<Link href={'/visit/museum-details/chiang-mai'}>
								<Button color={Button.color.PRIMARY}>{t('visitor-info')}</Button>
							</Link>
						</FeaturedItem>
					</div>

					<SectionColumn showTitle={false}></SectionColumn>
				</Section>
				<Section color="Dimmed">
					<VisitMuseums museums={filterMuseums} />
				</Section>
			</Layout>
		</>
	);
};

export const getServerSideProps: GetServerSideProps = async ({ locale, query }) => {
	const client = createClient({ locale });

	const clustersResponse = await client.findAll('user/story/cluster', {
		per_page: 1000,
	});

	const clusters = serializeCollectionResource(clustersResponse);

	const museumsResponse = await client.findAll<IMuseumResource>('museum', {
		per_page: 1000,
		filters: 'all',
	});

	const museums = serializeCollectionResource(museumsResponse);

	const pastMuseumsResponse = await client.findAll<IMuseumResource>('museum', {
		per_page: 1000,
		filters: 'past',
	});

	const pastMuseums = serializeCollectionResource(pastMuseumsResponse);

	const currentMuseumsResponse = await client.findAll<IMuseumResource>('museum', {
		per_page: 1000,
		filters: 'current',
	});

	const currentMuseums = serializeCollectionResource(currentMuseumsResponse);

	let filterMuseums = null;

	if (!query?.filter || query?.filter === MuseumFilterTypes.ALL || query?.filter === MuseumFilterTypes.UPCOMING) {
		const filterMuseumsResponse = await client.findAll<IMuseumResource>('museum', {
			per_page: 1000,
			filters: !query?.filter ? MuseumFilterTypes.EXHIBITION : query.filter,
		});
		filterMuseums = serializeCollectionResource(filterMuseumsResponse);
	}

	if (query?.filter === MuseumFilterTypes.PAST) {
		filterMuseums = pastMuseums;
	}

	if (query?.filter === MuseumFilterTypes.CURRENT) {
		filterMuseums = currentMuseums;
	}

	return {
		props: {
			museums,
			pastMuseums,
			currentMuseums,
			filterMuseums,
			clusters,
			...(await serverSideTranslations(getSafeLocale(locale), ['common'])),
		},
	};
};

export default VisitPage;
